<template lang="pug">
  .log-mood
    .log-mood-content
      .log-mood-header {{ !isTagScreenActive ? 'How are you feeling?' :"What word best describes your mood?" }}
      .feeling-emoji-container(v-if="!isTagScreenActive")
        .emoji-container(
            v-for="feeling in feelings"
            :key="feeling.id"
            :class="{ 'emoji-disabled-state': (mood && mood !== feeling.id) }"
          )
            .emoji-wrapper(@click="toogleMood(feeling)" :class="{ 'emoji-active-state': mood === feeling.id, 'emoji-inactive-state': mood && mood !== feeling.id}")
                WtSvgIcon(:icon="feeling.icon" width="48px" height="48px" style="width:48px;height:48px;")
            span.title {{ feeling.title }}
      .mood-tags-container(v-if="isTagScreenActive")
        BTaglist
           BTag(
                v-for="(tag, index) in tags"
                :key="index"
                :class="{ active: isSelectedTag(tag) }"
                @click="toggleTag(tag)"
            ) {{ tag }}

      .next-button(v-if="!isTagScreenActive")
        WtButton(
          @click.native="enableTagScreen"
          :disabled="!this.mood"
        ) Next
      .save-button(v-if="isTagScreenActive")
        WtButton(
            @click.native="saveEntry"
            :loading="savingMood"
        ) Next
</template>

<script>
import { mapActions } from "vuex";
import { dateUtilityMixin } from "@/mixins/dateUtilityMixin";
import analyticsTrackingMixin from "@/mixins/analyticsTrackingMixin";
import WtButton from "@@/WtButton/WtButton";
import WtTitle from "@@/WtTitle/WtTitle";
import WtSvgIcon from "@@/WtSvgIcon/WtSvgIcon";

export default {
  name: "LogMood",

  components: { WtTitle, WtButton, WtSvgIcon },

  mixins: [analyticsTrackingMixin, dateUtilityMixin],

  props: {
    uniqueKey: { type: String, default: null },
    entryItem: { type: Object, default: () => ({}), required: true },
    entryDate: { type: String, default: "" },
    formData: {
      type: FormData,
      required: true,
      validator: v => v instanceof FormData
    },
    saveEntryWrapper: { type: Function, required: true },
    disableSave: { type: Boolean, default: true },
    disableReset: { type: Boolean, default: true }
  },

  data() {
    return {
      isTagScreenActive: false,
      mood: null,
      savingMood: false,
      moodTextEntries: [],
      selectedTags: [],
      userInteracted: false,
      feelings: [
        { id: 5, icon: "super-emoji", title: "Great" },
        { id: 4, icon: "happy-emoji", title: "Good" },
        { id: 3, icon: "neutral-emoji", title: "Okay" },
        { id: 2, icon: "sad-emoji", title: "Low" },
        { id: 1, icon: "angry-emoji", title: "Really low" }
      ]
    };
  },

  computed: {
    moodText() {
      return this.selectedTags.join(", ");
    },

    currentMetricValue() {
      const metric = this._.find(this.entryItem.wellnessMetrics, {
        type: "mood"
      });
      return metric?.value ?? null;
    },

    currentMetricText() {
      const metric = this._.find(this.entryItem.wellnessMetrics, {
        type: "mood"
      });
      return metric?.text ?? "";
    },

    moodLabel() {
      return this.feelings.find(f => f.id === this.mood)?.title || "";
    },

    tags() {
      if (!Array.isArray(this.moodTextEntries) || !this.moodTextEntries.length)
        return [];
      const moodEntry = this.moodTextEntries.find(
        e => e.header === this.moodLabel
      );
      return moodEntry?.entries || [];
    },

    setFormData() {
      if (!this._.isEmpty(this.entryItem)) {
        return (
          this.mood !== this.currentMetricValue ||
          this.moodText !== this.currentMetricText
        );
      }
      return true;
    }
  },

  watch: {
    mood() {
      if (this.setFormData) this.updateFormData();
    },
    moodText() {
      if (this.setFormData) this.updateFormData();
    },

    currentMetricText(newVal) {
      if (!this.userInteracted) {
        this.selectedTags = newVal ? newVal.split(",").map(t => t.trim()) : [];
      }
    },

    entryItem: {
      handler() {
        if (!this.userInteracted) {
          if (this.currentMetricValue) {
            this.mood = this.currentMetricValue; // load saved mood
          } else {
            this.mood = null; // clear mood if no saved value
            this.selectedTags = []; // also clear tags
          }
        }
      },
      immediate: true,
      deep: true
    },

    uniqueKey: {
      handler() {
        if (!this.userInteracted) {
          if (this.currentMetricValue) {
            this.mood = this.currentMetricValue; // load saved mood
          } else {
            this.mood = null; // clear mood if no saved value
            this.selectedTags = [];
          }
        }
      },
      immediate: true
    }
  },

  async mounted() {
    this.trackFeatureView("tracking", "tracking_mood");
    await this.fetchMoodTextEntries();
    if (this.currentMetricText) {
      this.selectedTags = this.currentMetricText.split(",").map(t => t.trim());
    }
    if (this.currentMetricValue) {
      this.mood = this.currentMetricValue;
    }
  },

  methods: {
    ...mapActions("members", ["storeJournal", "updateJournal"]),

    updateFormData() {
      this.formData.set("mood", this.mood);
      this.formData.set("moodText", this.moodText);
      this.$emit("formDataUpdated", true);
    },

    isSelectedTag(tag) {
      return this.selectedTags.includes(tag);
    },

    toggleTag(tag) {
      this.userInteracted = true;
      this.selectedTags = this.selectedTags[0] === tag ? [] : [tag];
    },

    toogleMood(feeling) {
      if (this.mood && this.mood !== feeling.id) {
        return;
      }
      this.userInteracted = true;
      if (this.mood === feeling.id) {
        this.mood = null;
        this.selectedTags = [];
      } else {
        this.mood = feeling.id;
        if (this.mood !== this.currentMetricValue) {
          this.selectedTags = [];
        }
      }
    },

    enableTagScreen() {
      this.isTagScreenActive = true;
    },

    async fetchMoodTextEntries() {
      try {
        const { data } = await this.axios.get("journals/mood-entries");
        this.moodTextEntries = data;
      } catch (err) {
        console.error("Failed to load mood text entries:", err);
      }
    },

    async saveEntry() {
      // Always send the values so the backend links the post to this metric
      this.formData.set("mood", this.mood);
      this.formData.set("moodText", this.moodText);

      this.savingMood = true;
      try {
        await this.saveEntryWrapper(this.storeJournal, this.updateJournal);
      } finally {
        this.savingMood = false;
      }
    }
  }
};
</script>

<style lang="sass" scoped>
.log-mood
  height: 100%
  display: flex
  flex-direction: column
  justify-content: space-between

.log-mood-content
  flex: 1
  flex-direction: column
  display: flex
  justify-content: center
  align-items: center
  gap: 20px

  .log-mood-header
    width: 100%
    text-align: left
    font-weight: 600
    font-size: 20px
    line-height: 28px
    letter-spacing: 0px
    color: #1B1D20

  .feeling-emoji-container
    display: flex
    flex-direction: row
    gap: 10px
    justify-content: space-between
    width: 100%

    .emoji-disabled-state
         opacity: 0.4

    .emoji-container
       display: flex
       flex-direction: column
       gap: 6px
       align-items: center
       cursor: pointer

       .title
          font-weight: 400
          font-size: 13px
          line-height: 18px
          letter-spacing: 0px
          text-align: center
          color: #1B1D20

       .emoji-active-state
          border-radius: 33px
          border: 2px solid #2972F0

       .emoji-inactive-state
           cursor: not-allowed !important

  .mood-tags-container
    ::v-deep .tags
        gap: 8px
        justify-content: center

    ::v-deep .tag
        height: 32px
        angle: 0 deg
        margin: 0
        border-radius: 4px
        padding-top: 6px
        padding-right: 14px
        padding-bottom: 6px
        padding-left: 14px
        background-color: #E8EBF2
        font-weight: 500
        font-size: 14px
        line-height: 20px
        letter-spacing: 0px
        text-align: center
        color: #1B1D20
        cursor: pointer

    ::v-deep .active
        background-color: #30A250 !important
        color: #FFFFFF !important

    .emoji-disabled-state
        opacity: 0.4

  .save-button
    width: 100%

    ::v-deep .button
      width: 100% !important
      justify-content: center
      font-size: 16px
      line-height: 24px
      letter-spacing: 0px
      background-color: #2972F0
      font-weight: 600

      &:disabled
        background-color: #D1D5DB !important
        border-color: #D1D5DB !important
        color: #9CA3AF !important
        cursor: not-allowed

  .next-button
    width: 100%

    ::v-deep .button
      width: 100% !important
      justify-content: center
      font-size: 16px
      line-height: 24px
      letter-spacing: 0px
      background-color: #2972F0
      font-weight: 600

      &:disabled
        background-color: #D1D5DB !important
        border-color: #D1D5DB !important
        color: #9CA3AF !important
        cursor: not-allowed
</style>
